/**
 * Helper functions for PathBuilder component
 * Extracted to reduce complexity in main component
 */

import { getProductConfig } from '../config/products';

/**
 * Get product name for display (translated)
 * @param {string} productId - Product ID
 * @param {Function} t - Translation function
 * @returns {string} Translated product name
 */
export const getProductName = (productId, t) => {
  if (!productId) return 'JavaCard';
  
  const productKey = productId.charAt(0).toUpperCase() + productId.slice(1);
  return t(`pathBuilder.checkboxes.${productId}`) || productKey;
};

/**
 * Get product repository name
 * @param {string} productId - Product ID
 * @returns {string} Repository name
 */
export const getProductRepoName = (productId) => {
  const config = getProductConfig(productId);
  return config.repoName;
};

/**
 * Get product CAP file name
 * @param {string} productId - Product ID
 * @returns {string} CAP file name
 */
export const getProductCapFileName = (productId) => {
  const config = getProductConfig(productId);
  return config.capFileName;
};

/**
 * Get product repository URL
 * @param {string} productId - Product ID
 * @returns {string} Repository URL
 */
export const getProductRepoUrl = (productId) => {
  const config = getProductConfig(productId);
  return config.repoUrl;
};

/**
 * Get translated OS name
 * @param {Object} options - Options object with OS flags
 * @param {Function} t - Translation function
 * @returns {string} Translated OS name
 */
export const getOSName = (options, t) => {
  if (options.osWindows) return t("pathBuilder.checkboxes.windows");
  if (options.osMac) return t("pathBuilder.checkboxes.mac");
  if (options.osLinux) return t("pathBuilder.checkboxes.linux");
  return '';
};

/**
 * Get OS name for download component (English names)
 * @param {Object} options - Options object with OS flags
 * @returns {string|null} OS name or null
 */
export const getSelectedOS = (options) => {
  if (options.osWindows) return 'Windows';
  if (options.osMac) return 'Mac';
  if (options.osLinux) return 'Linux';
  return null;
};

/**
 * Check if any OS is selected
 * @param {Object} options - Options object with OS flags
 * @returns {boolean} True if any OS is selected
 */
export const hasOSSelected = (options) => {
  return options.osWindows || options.osMac || options.osLinux;
};

/**
 * Get initial state for PathBuilder options
 * @returns {Object} Initial state object
 */
export const getInitialOptionsState = () => ({
  // SHORTCUTS
  shortestPath: false,
  dabbler: false,
  rockstarDev: false,
  // OS SELECTION
  osWindows: false,
  osMac: false,
  osLinux: false,
  // SEEDSIGNER
  buySeedSigner: false,
  assembleSeedSigner: false,
  downloadInstallBalena: false,
  flashSeedSignerUseRelease: false,
  flashSeedSignerCompile: false,
  flashSeedSigner: false,
  configureSeedSignerSettings: false,
  // HAT
  buyHat: false,
  buildHat: false,
  integrateHat: false,
  print3DSeedSignerHatBox: false,
  // JAVACARD - Product Selection (radio buttons - only one selected)
  javaCardProduct: null, // 'satochip' | 'seedkeeper' | 'satodime'
  // JAVACARD - Card Acquisition (Level 2)
  buyJavaCardReady: false, // Buy ready-made card (pre-flashed)
  buyJavaCardBlank: false, // Buy blank card (to flash yourself)
  // JAVACARD - Flashing Method (Level 3 - only if blank card)
  flashJavaCardViaSeedSigner: false,
  flashJavaCardDesktopCompile: false,
  flashJavaCardDesktopDownload: false,
  // JAVACARD - Verification (Level 5 - only if download release)
  verifySignatureJavaCardDesktop: false,
  // JAVACARD - Common Operations (Level 6 - after card is ready)
  addRemoveApplet: false,
  removeAppletViaSeedSigner: false,
  // JAVACARD - Product-Specific Operations
  // Seedkeeper operations (only when Seedkeeper selected)
  createSeedkeeper: false,
  useSeedkeeper: false,
  backupSeedkeeper: false,
  restoreFromSeedkeeper: false,
  // SEEDSIGNER + SATOCHIP INTERACTIONS
  storeKeyOnSatochip: false,
  signTransactionWithSatochip: false,
  importKeyFromSatochip: false,
  backupSeedToSatochip: false,
  // SEEDSIGNER + SEEDKEEPER INTERACTIONS
  importKeyFromSeedkeeper: false,
  backupSeedToSeedkeeper: false,
  // SEEDOR.IO BACKUP
  backupSeedSeedor: false,
  // BLUEWALLET
  downloadBlueWallet: false,
  // BULL WALLET
  downloadBullWallet: false,
  // SPARROW WALLET
  downloadSparrowWallet: false,
  configureSeedSignerForBlueWallet: false,
  exportXpubToBlueWallet: false,
  importWalletIntoBlueWallet: false,
  receiveBitcoinInBlueWallet: false,
  createTransaction: false,
  broadcastTransaction: false,
  verifyTransaction: false,
  // VERIFY SIGNATURE (inside Use release)
  verifySignature: false, // For SeedSigner - basic SHA256 checksum verification
  verifyBitcoinSignature: false // For SeedSigner - Bitcoin message signature verification (advanced)
});

